import { MACHINE_TILE_IMAGE } from '../../demo/demoMode';
import { Checkbox } from '../../components/Checkbox';
import { MobileListItemConfig } from '../../components/data-display/ResponsiveDataView';
import { cn } from '../../utils/className';
import { Machine } from '../../types/machines/machine.types';
import { getMachineLocation, getMachineShortId } from './machineIdentity';
import { MachineStatusBadge } from './MachineStatusBadge';

interface MachineListItemMobileProps {
  machine: Machine;
  config: MobileListItemConfig<Machine>;
}

export function MachineListItemMobile({
  machine,
  config,
}: MachineListItemMobileProps) {
  const { isSelected, selectionMode, onSelect, onClick, isActive } = config;

  const name = machine.displayName || machine.machineId || 'Unknown';
  const shortId = getMachineShortId(machine);
  const location = getMachineLocation(machine);

  // The checkbox sits beside the row's button. A control nested in a button
  // is invalid, and a screen reader cannot reach it.
  return (
    <div
      className={cn(
        'flex w-full max-w-full items-center transition-colors',
        'hover:bg-surface-primary-support active:bg-surface-primary-support',
        isActive && 'bg-surface-focus-support'
      )}
    >
      {/* Mounted outside selection mode too, so the checkbox can slide in and
          push the row over instead of making it jump. */}
      <div
        inert={!selectionMode}
        className={cn(
          `shrink-0 self-start overflow-hidden transition-[width,opacity]
          duration-200 ease-in-out motion-reduce:transition-none`,
          selectionMode ? 'w-10 opacity-100' : 'w-0 opacity-0'
        )}
      >
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onSelect?.(machine.id)}
          aria-label={`Select ${name}`}
          className='border-border-primary mt-9 ml-4 size-6 rounded-[5px]
            [&_svg]:size-3.5'
        />
      </div>

      <button
        type='button'
        onClick={() => onClick?.(machine)}
        aria-current={isActive ? 'true' : undefined}
        className={cn(
          'flex min-w-0 flex-1 cursor-pointer items-start gap-4 p-4 text-left',
          `focus-visible:ring-focus focus-visible:ring-2
          focus-visible:outline-none focus-visible:ring-inset`,
          selectionMode && 'pl-4'
        )}
      >
        <div
          className='bg-surface-focus-support flex size-16 shrink-0 items-center
            justify-center rounded-lg p-2.5'
        >
          <img
            // The design gives every row the same render, so it says nothing
            // about the machine and stays out of the accessibility tree.
            src={MACHINE_TILE_IMAGE}
            alt=''
            className='h-full w-full object-contain'
            loading='lazy'
            decoding='async'
          />
        </div>

        <div className='flex min-w-0 flex-1 flex-col items-start gap-2'>
          <div className='flex w-full min-w-0 flex-col'>
            <h3 className='size-p1 text-focus w-full truncate font-bold'>
              {name}
            </h3>

            {shortId && (
              <span
                className='size-caption text-secondary w-full truncate
                  font-bold'
              >
                {shortId}
              </span>
            )}

            {location && (
              <p className='size-caption text-secondary w-full truncate'>
                {location}
              </p>
            )}
          </div>

          {/* EDMS's `state`, verbatim - same as the table. The synthetic-offline
              disclosure lives in the machine side panel, not on this row. */}
          <MachineStatusBadge status={machine.status} />
        </div>
      </button>
    </div>
  );
}
